import { css } from '@emotion/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faPaperPlane, faPencil, faPhone, faWindow,
} from '@fortawesome/pro-solid-svg-icons';
import {
  forwardRef, type KeyboardEvent, type MouseEvent, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import {
  InputSize, TextInputComponent,
} from '~/_shared/baseComponents/inputs/textInput/textInput.component';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { guaranteeHash } from '~/_shared/components/colorPicker/colorPicker.helpers';
import { HoverableIconComponent } from '~/_shared/components/icons/hoverableIcon.component';
import { LimitNumberOfLinesComponent } from '~/_shared/components/limitNumberOfLines/limitNumberOfLines.component';
import { useHover } from '~/_shared/hooks/interaction/useHover';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { ColumnRole } from '~/_shared/types/columnRole.enum';
import type { SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  getIsPhoneNumber, validateEmail,
} from '~/_shared/utils/throttle/validation/validation.helpers';
import { getItemStyles } from '~/customizeLocationPanel/customizeLocationPanel.constants';
import { type BubbleEditorItem } from '../locationData.types';
import { type ColumnStyleSettingsData } from '../panelSettings.types';

type FontProps = {
  fontColor: string;
  fontSize: string;
};

const textCommonStyle = ({ fontColor, fontSize }: FontProps) => css({
  color: guaranteeHash(fontColor),
  fontSize: `${fontSize}px`,
  paddingLeft: 8,
  textDecoration: 'none',
});

const ellipsisStyle = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const contactDataWrapperStyle = (fontProps: FontProps) => css(textCommonStyle(fontProps), {
  alignItems: 'center',
  display: 'flex',
});

const basicDataRecordWrapperStyle = (fontProps: FontProps) => css(
  textCommonStyle(fontProps),
  { paddingLeft: 0 }
);

const linkStyle = (fontProps: FontProps, indent: boolean) => css(
  ellipsisStyle,
  textCommonStyle(fontProps), {
    display: 'block',
    paddingLeft: indent ? undefined : 0,
    width: '100%',
  }
);

const iconsStyle = css({
  fontSize: 'calc(100% + 2px)',
  fontWeight: 900,
  lineHeight: '24px',
  marginRight: 8,
});

const dataBodyRecordStyle = ({ verticalGap, ...fontProps }: FontProps & {
  verticalGap: string;
}) => css(textCommonStyle(fontProps), {
  marginTop: `${verticalGap}px`,
});

const labelStyle = css({ textTransform: 'uppercase' });

const HOVER_HIT_AREA_EXPANSION = 4;

const valueRowStyle = css({
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  minWidth: 0,
  position: 'relative',
  zIndex: 0,

  '&::after': {
    content: '""',
    inset: `-${HOVER_HIT_AREA_EXPANSION}px 0`,
    position: 'absolute',
    zIndex: -1,
  },
});

const editIconStyle = ({ isVisible }: { isVisible: boolean }) => css({
  flexShrink: 0,
  marginLeft: 6,
  opacity: isVisible ? 1 : 0,
  pointerEvents: isVisible ? 'auto' : 'none',
  transition: 'opacity 0.15s',
});

const editInputStyle = css({
  flex: 1,
  height: 26,
  minWidth: 0,
});

const valueContentStyle = css({
  flex: 1,
  minWidth: 0,
});

type BubbleItemProps = Readonly<{
  item: Required<BubbleEditorItem>;
  bulkStyles: ColumnStyleSettingsData;
  isDraggable: boolean;
  openLinksInNewTab?: boolean;
  onEdit?: (columnId: SpreadsheetColumnId, newValue: string) => void;
}>;

export const BubbleItemComponent = forwardRef<HTMLDivElement, BubbleItemProps>(({
  item, isDraggable, openLinksInNewTab, bulkStyles, onEdit,
}, ref) => {
  const [icon] = useState(getRelevantIcon(item.itemType));
  const [t] = useTranslation();
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const { hoverProps, isHovered } = useHover({ isDisabled: !onEdit });
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(item.value);
  const [internalValue, setInternalValue] = useState(item.value);

  const { labelFontColor, labelFontSize, showLabel, valueFontColor, valueFontSize, verticalGap } = getItemStyles(item, bulkStyles);
  const valueFontProps = { fontColor: valueFontColor, fontSize: valueFontSize };
  const labelFontProps = { fontColor: labelFontColor, fontSize: labelFontSize };

  const handleMouseDownCallback = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (isDraggable) {
        e.preventDefault();
      }
    },
    [isDraggable],
  );

  const isTextLinkTestResults = useMemo(() => {
    const isWebLink = /^(?:https?:\/\/|www\.)/.test(internalValue);
    const isEmailLink = validateEmail(internalValue);
    const isPhoneNumber = getIsPhoneNumber(internalValue);
    return {
      isWebLink,
      isEmailLink,
      isPhoneNumber,
      isLink: isWebLink || isEmailLink || isPhoneNumber,
    };
  }, [internalValue]);

  const isLink = useMemo(() => {
    if (item.itemType && [ColumnRole.Email, ColumnRole.URL, ColumnRole.Image].includes(item.itemType)) {
      return true;
    }

    return isTextLinkTestResults.isLink;
  }, [isTextLinkTestResults.isLink, item.itemType]);

  const hrefLink = useMemo(() => {
    if (item.itemType === ColumnRole.Email || isTextLinkTestResults.isEmailLink) {
      return `mailto:${internalValue}`;
    }
    if (isTextLinkTestResults.isWebLink) {
      if (internalValue.startsWith('http://') || internalValue.startsWith('https://')) {
        return internalValue;
      }
      else {
        return `//${internalValue}`;
      }
    }
    if (isTextLinkTestResults.isPhoneNumber) {
      return `tel:${internalValue}`;
    }
    return internalValue;
  }, [isTextLinkTestResults, item.itemType, internalValue]);

  const onValueChange = useCallback((newValue: string) => {
    if (item.id && item.value !== newValue) {
      // optimistically set the new value, if it gets reverted, user should see an error
      setInternalValue(newValue);
      onEdit?.(item.id, newValue);
    }
  }, [item.id, item.value, onEdit]);

  const handleEditIconClick = useCallback((e: MouseEvent<Element>) => {
    e.preventDefault();
    e.stopPropagation();
    setEditValue(internalValue);
    setIsEditing(true);
  }, [internalValue]);

  const handleValueDoubleClick = useCallback((e: MouseEvent<Element>) => {
    if (!onEdit) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setEditValue(internalValue);
    setIsEditing(true);
  }, [internalValue, onEdit]);

  const handleEditInputBlur = useCallback(() => {
    setIsEditing(false);
    onValueChange(editValue);
  }, [editValue, onValueChange]);

  const handleEditInputKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const isContactItem = !!icon;

  const wrapperStyle = isContactItem
    ? contactDataWrapperStyle(valueFontProps)
    : basicDataRecordWrapperStyle(labelFontProps);

  const itemStyle = isContactItem || isLink
    ? linkStyle(valueFontProps, !isContactItem)
    : dataBodyRecordStyle({ ...valueFontProps, verticalGap });

  useEffect(() => {
    setInternalValue(item.value);
  }, [item.value]);

  return (
    <div
      css={wrapperStyle}
      ref={ref}
      onMouseDown={handleMouseDownCallback}
      draggable={false}
    >
      {icon && <FontAwesomeIcon icon={icon} css={iconsStyle} />}

      {!isContactItem && showLabel && (
        <div css={labelStyle}>{item.itemData.name}</div>
      )}

      <div css={valueRowStyle} {...hoverProps}>
        {isEditing ? (
          <TextInputComponent
            ref={inputRef}
            autoFocus
            css={editInputStyle}
            icon={null}
            size={InputSize.Xs}
            value={editValue}
            onBlur={handleEditInputBlur}
            onChange={setEditValue}
            onKeyDown={handleEditInputKeyDown}
            onMouseDown={e => e.stopPropagation()}
          />
        ) : (
          <>
            {isLink && !isDraggable ? (
              <a
                href={hrefLink}
                target={openLinksInNewTab ? '_blank' : undefined}
                css={[itemStyle, valueContentStyle]}
                rel="noreferrer"
                onDoubleClick={handleValueDoubleClick}
              >
                {internalValue}
              </a>
            ) : (
              <LimitNumberOfLinesComponent
                maxNumberOfLines={isDraggable ? 4 : 1000}
                css={[itemStyle, valueContentStyle]}
                onDoubleClick={handleValueDoubleClick}
              >
                {internalValue}
              </LimitNumberOfLinesComponent>
            )}

            {onEdit && (
              <TooltipComponent tooltipContent={t('Edit')}>
                <HoverableIconComponent
                  icon={faPencil}
                  title={t('Edit')}
                  size={12}
                  baseColor={theme.iconColors.secondary}
                  hoverColor={theme.iconColors.focused}
                  css={editIconStyle({ isVisible: isHovered })}
                  onClick={handleEditIconClick}
                />
              </TooltipComponent>
            )}
          </>
        )}
      </div>
    </div>
  );
});

const getRelevantIcon = (type: ColumnRole | null): IconProp | null => {
  switch (type) {
    case ColumnRole.Phone:
      return faPhone;
    case ColumnRole.Email:
      return faPaperPlane;
    case ColumnRole.URL:
      return faWindow;

    default:
      return null;
  }
};
